import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { OtaFooter } from '@ota/design-system';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from '@ota/api-client';
import { authStore, changePassword } from '../api';
import { PublicHeader } from '../components/PublicHeader';

export function MyProfilePage() {
  const navigate = useNavigate();
  const session = authStore.get();

  // Password form
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [pwSaved, setPwSaved] = useState(false);

  const pwMutation = useMutation({
    mutationFn: () => changePassword(pwForm.current, pwForm.next),
    onSuccess: () => {
      setPwForm({ current: '', next: '', confirm: '' });
      setPwSaved(true);
      setTimeout(() => setPwSaved(false), 3000);
    },
  });

  const pwError = (() => {
    if (!pwForm.next) return null;
    if (pwForm.next.length < 8) return 'New password must be at least 8 characters';
    if (pwForm.next !== pwForm.confirm) return 'Passwords do not match';
    return null;
  })();

  // Not logged in
  if (!session) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PublicHeader />
        <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 }, flex: 1 }}>
          <Card>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ mb: 1 }}>Sign in to view your profile</Typography>
              <Button variant="contained" color="secondary" sx={{ mt: 2 }} onClick={() => navigate('/login?returnTo=/my-profile')}>
                Sign in
              </Button>
            </CardContent>
          </Card>
        </Container>
        <OtaFooter />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 }, flex: 1 }}>
        <Stack spacing={3}>
          <Stack spacing={0.5}>
            <Typography variant="overline" color="secondary.main">Account</Typography>
            <Typography variant="h3">My profile</Typography>
          </Stack>

          {/* ── Account details ─────────────────────────────────── */}
          <Card>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <AccountCircleIcon sx={{ fontSize: 48, color: 'secondary.main' }} />
                <Stack>
                  <Typography variant="h5">{session.user.fullName ?? 'Guest'}</Typography>
                  <Typography variant="body2" color="text.secondary">{session.user.email}</Typography>
                </Stack>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full name"
                    value={session.user.fullName ?? ''}
                    disabled
                    helperText="Contact support to update your name"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email address"
                    value={session.user.email}
                    disabled
                    helperText="Email cannot be changed"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account type"
                    value={(session.user.roles ?? []).join(', ')}
                    disabled
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* ── Change password ──────────────────────────────────── */}
          <Card>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <LockOutlinedIcon color="action" />
                <Typography variant="h5">Change password</Typography>
              </Stack>

              {pwSaved && (
                <Alert severity="success" sx={{ mb: 2 }}>Password updated successfully.</Alert>
              )}
              {pwMutation.isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {pwMutation.error instanceof ApiError
                    ? pwMutation.error.body?.message ?? pwMutation.error.message
                    : pwMutation.error instanceof Error
                      ? pwMutation.error.message
                      : 'Failed to update password'}
                </Alert>
              )}

              <Grid container spacing={2} sx={{ maxWidth: 480 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current password"
                    type={showCurrent ? 'text' : 'password'}
                    value={pwForm.current}
                    onChange={(e) => setPwForm((p) => ({ ...p, current: e.target.value }))}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowCurrent((v) => !v)}>
                            {showCurrent ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New password"
                    type={showNext ? 'text' : 'password'}
                    value={pwForm.next}
                    onChange={(e) => setPwForm((p) => ({ ...p, next: e.target.value }))}
                    helperText="Minimum 8 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowNext((v) => !v)}>
                            {showNext ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm new password"
                    type="password"
                    value={pwForm.confirm}
                    onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))}
                    error={!!pwError && !!pwForm.confirm}
                    helperText={pwForm.confirm ? (pwError ?? ' ') : ' '}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={
                    pwMutation.isPending ||
                    !pwForm.current ||
                    !pwForm.next ||
                    !pwForm.confirm ||
                    !!pwError
                  }
                  onClick={() => pwMutation.mutate()}
                  startIcon={pwMutation.isPending ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {pwMutation.isPending ? 'Updating…' : 'Update password'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Container>
      <OtaFooter />
    </Box>
  );
}
